'use client';

import api from './api';
import { API_URLS } from './api-urls';

type ApiEnvelope<T> = {
    success: boolean;
    data?: T;
    statusCode?: number;
    errorMessage?: string | null;
};

export type QuickReadAloudPrompt = {
    promptId: string;
    paragraph: string;
    examName: string;
    isActive: boolean;
};

export type QuickReadAloudMark = {
    title: string;
    score: number;
    outOf: number;
    scoreOutOf90?: number;
};

export type QuickReadAloudToken = {
    token: string;
    grade: 'High' | 'Medium' | 'Low' | string;
    hasBreak: boolean;
    tempo: string;
    breakQuality: string;
};

export type QuickReadAloudResult = {
    transcript?: string | null;
    promptBody?: string | null;
    audioDuration?: number | null;
    markTableResult?: QuickReadAloudMark[] | null;
    oralFluencyReview?: {
        totalSeconds?: number;
        pauseEventCount?: number;
        transcript?: string;
        tokenResults?: QuickReadAloudToken[];
    } | null;
};

export type QuickReadAloudFeedback = {
    focusNow: string;
    summary: string;
    whyItMatters: string;
    nextAttemptFocus: string;
    practiceTargets: string[];
};

export type QuickReadAloudSubmission = {
    requestId: string;
    result: QuickReadAloudResult;
    feedback?: QuickReadAloudFeedback | null;
};

function unwrap<T>(payload: ApiEnvelope<T>, fallbackMessage: string): T {
    if (!payload?.success || payload.data == null) {
        throw new Error(payload?.errorMessage?.trim() || fallbackMessage);
    }

    return payload.data;
}

export async function getQuickReadAloudPrompt() {
    const response = await api.get<ApiEnvelope<QuickReadAloudPrompt>>(
        API_URLS.QUICK_READ_ALOUD_PROMPT,
    );

    return unwrap(response.data, 'Speaking practice is not available right now.');
}

export async function submitQuickReadAloud(
    file: File,
    audioDuration: number,
) {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('audioDuration', audioDuration.toFixed(2));

    const response = await api.post<ApiEnvelope<QuickReadAloudSubmission>>(
        API_URLS.QUICK_READ_ALOUD_SUBMIT,
        formData,
        { timeout: 120000 },
    );

    return unwrap(
        response.data,
        'We could not analyse this recording. Please record again and retry.',
    );
}
